// Pre-existing native import - exempt from multi-version OTA adapter layer requirement.
// New usages should be migrated to the adapter layer.
// eslint-disable-next-line no-restricted-imports
import { App, type AppInfo } from "@capacitor/app";
import {
  type DeviceInfo,
  getDeviceId,
  getDeviceInfo as getDeviceInfoAdapter,
} from "@src/appV2/NativeAdapters/device.adapter";
// Pre-existing native import - exempt from multi-version OTA adapter layer requirement.
// New usages should be migrated to the adapter layer.
// eslint-disable-next-line no-restricted-imports
import { Deploy } from "cordova-plugin-ionic";

import { APP_V2_APP_EVENTS, logError } from "../../analytics";
import { isCapacitorPlatform } from "./platformUtils";

export interface CbhAppInfo extends AppInfo {
  otaBuildId: string;
}

/**
 * Return an always defined `AppInfo` blob, even if, for some reason, Capacitor
 * errors out.
 * For non-capacitor platform, return a generic `AppInfo`.
 * Since we want this to "always work", this function does not reject the promise.
 */
export async function getAppInfo(): Promise<CbhAppInfo> {
  if (isCapacitorPlatform()) {
    try {
      const appInfo = await App.getInfo();
      const currentOtaVersion = await Deploy.getCurrentVersion();
      return {
        ...appInfo,
        otaBuildId: currentOtaVersion?.buildId?.toString() ?? "",
      };
    } catch (error) {
      logError(APP_V2_APP_EVENTS.APP_INFO_ERROR, { error });
    }
  }

  return {
    name: "CBH Mobile App",
    version: "",
    build: "",
    id: "",
    otaBuildId: "",
  };
}

export interface PartialDeviceInfo extends Pick<DeviceInfo, "platform" | "osVersion"> {
  deviceId: string;
}

/**
 * Return an always defined `PartialDeviceInfo` blob, even if, for some reason, Capacitor
 * errors out.
 * For non-capacitor platform, return a generic `PartialDeviceInfo`.
 * Since we want this to "always work", this function does not reject the promise.
 */
export async function getDeviceInfo(): Promise<PartialDeviceInfo> {
  const deviceIdResult = await getDeviceId();

  if (isCapacitorPlatform()) {
    try {
      const deviceInfo = await getDeviceInfoAdapter();
      return {
        ...deviceInfo,
        deviceId: deviceIdResult.identifier,
      };
    } catch (error) {
      logError(APP_V2_APP_EVENTS.DEVICE_INFO_ERROR, { error });
    }
  }

  return {
    platform: "web",
    osVersion: "web-unknown",
    // For web, we show a random string that persists in localStorage
    deviceId: deviceIdResult.identifier,
  };
}
